import { type Level } from '../models/level';
import {
  isWorldOpen,
  levelId,
  problemTypeLabel,
  WORLDS,
  worldById,
  type World,
} from '../models/world';
import { loadLevel } from '../services/levelLoader';
import {
  isUnlocked,
  loadProgress,
  starsFor,
  totalStars,
  worldStars,
  type ProgressMap,
} from '../services/progress';
import {
  PuzzleScreen,
  type PuzzlePresentation,
  type PuzzleResult,
  calendarMarkup,
  calendarSheet,
  escapeHtml,
} from './puzzleScreen';
import { playCutscene } from './cutsceneScreen';
import { EPILOGUE_CUTSCENE_ID, worldCutsceneId } from '../story/cutsceneIds';
import { hasSeenCutscene, seenCutscenes } from '../services/progress';
import { audio } from '../audio/audioService';
import {
  initSync,
  recordCutsceneSeen,
  recordRun,
  registerAccount,
  renameRunner,
  signIn,
  signOut,
  type SyncState,
} from '../services/sync';
import { type ApiUser } from '../services/api';
import { accountMarkup, readCredentials, setFormBusy, showFormError } from './accountScreen';
import { achievementById } from '../game/achievements';
import { tutorialCardsForLevel, tutorialId, type TutorialCard } from '../game/tutorialData';
import { type MapScene } from '../render/cityMap';
import { themeFor, type MapTheme } from '../render/theme';
import { MapPanel } from './mapPanel';
import { districtScene, gearfallMap, runPinId, worldPinId } from '../render/geography';
import {
  dailyChallengeForToday,
  dailyStreakFromProgress,
  generateDailyChallengeLevel,
  type DailyChallenge,
} from '../game/dailyChallenge';
import { generateRandomLevel } from '../game/levelGenerator';
import {
  dailyBriefingMarkup,
  formatDailyDate,
  formatDailyDateShort,
  streakMarkup,
} from './dailyScreen';
import { filterGlossaryList, glossaryHash, glossaryMarkup, unsealedCount } from './glossaryScreen';
import { GLOSSARY, glossaryTermForProblemType } from '../game/glossaryData';
import { castMarkup, chronicleMarkup } from './chronicleScreen';
import { settingsMarkup, showFormStatus } from './settingsScreen';
import { normalizeRunnerName, suggestCallsign } from '../game/callsigns';
import { ensureRunnerName } from '../services/runner';
import { chronicleChapters, chronicleTally, spokenCharacters } from '../story/chronicle';
import { EMPTY_ROSTER } from '../story/characters';
import { loadCharacters, loadCutscene } from '../story/storyLoader';
import {
  endlessHash,
  endlessSetupMarkup,
  loadEndlessSetup,
  newSeed,
  rankFor,
  readEndlessSetup,
  saveEndlessSetup,
  type EndlessSetup,
} from './endlessScreen';

const ORNAMENT = `
  <div class="ornament" aria-hidden="true">
    <i></i>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <circle cx="12" cy="12" r="8.5" stroke-width="5" stroke-dasharray="3.34 3.34" />
      <circle cx="12" cy="12" r="6.5" stroke-width="3" />
      <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
    </svg>
    <i></i>
  </div>
`;

/** City behind the paper screens, so the picture stays continuous between routes. */
const BACKDROP = `
  <div class="stage stage--dim map--city" aria-hidden="true">
    <canvas class="map__canvas"></canvas>
  </div>
`;

type Route =
  | { name: 'worlds' }
  | { name: 'levels'; worldIndex: number }
  | { name: 'puzzle'; worldIndex: number; levelIndex: number }
  | { name: 'daily' }
  | { name: 'dailyPlay' }
  | { name: 'endless' }
  | { name: 'endlessPlay'; setup: EndlessSetup; seed: number }
  | { name: 'account' }
  | { name: 'glossary'; slug: string | null }
  | { name: 'chronicle' }
  | { name: 'settings' };

/** What a finished run belongs to; decides how the report reads and where its buttons lead. */
type RunContext =
  | { kind: 'campaign'; worldIndex: number }
  | { kind: 'daily'; challenge: DailyChallenge }
  | { kind: 'endless'; setup: EndlessSetup; seed: number };

export class App {
  private readonly root: HTMLElement;
  private progress: ProgressMap;
  private activePuzzle: PuzzleScreen | null = null;
  private activeMap: MapPanel | null = null;
  private online = false;
  private user: ApiUser | null = null;
  private homeTitleCompact = false;
  /** Settles once the account has answered (or could not be reached). */
  private sessionReady: Promise<void> = Promise.resolve();

  constructor(root: HTMLElement) {
    this.root = root;
    this.progress = loadProgress();
  }

  start(): void {
    audio.bindUnlockGesture();
    window.addEventListener('hashchange', () => this.renderRoute());
    this.sessionReady = this.openSession();
    this.renderRoute();
  }

  private async openSession(): Promise<void> {
    this.adopt(await initSync());

    const route = this.parseHash().name;
    if (['worlds', 'levels', 'account', 'settings', 'glossary', 'chronicle'].includes(route)) {
      this.renderRoute();
    }
  }

  private adopt(state: SyncState): void {
    this.progress = state.progress;
    this.online = state.online;
    this.user = state.user;
  }

  /**
   * Waits for the account before deciding: what another device has already
   * seen must not play again here. `stillWanted` lets a caller withdraw the
   * beat when the player has moved on in the meantime.
   */
  private async playStoryBeatOnce(
    cutsceneId: string,
    stillWanted: () => boolean = () => true,
  ): Promise<void> {
    await this.sessionReady;
    if (hasSeenCutscene(cutsceneId) || !stillWanted()) return;
    void recordCutsceneSeen(cutsceneId);
    try {
      await playCutscene(cutsceneId);
    } catch {}
  }

  /** The last district's aftermath leads straight into the epilogue. */
  private async playWorldOutro(worldIndex: number): Promise<void> {
    await this.playStoryBeatOnce(worldCutsceneId(worldIndex, 'outro'));
    if (worldIndex === WORLDS.at(-1)?.id) await this.playStoryBeatOnce(EPILOGUE_CUTSCENE_ID);
  }

  private navigate(hash: string): void {
    if (window.location.hash === hash) this.renderRoute();
    else window.location.hash = hash;
  }

  private parseHash(): Route {
    const hash = window.location.hash.replace(/^#\/?/, '');
    const levelMatch = hash.match(/^world\/(\d+)\/level\/(\d+)$/);
    if (levelMatch) {
      return {
        name: 'puzzle',
        worldIndex: Number(levelMatch[1]),
        levelIndex: Number(levelMatch[2]),
      };
    }
    const worldMatch = hash.match(/^world\/(\d+)$/);
    if (worldMatch) return { name: 'levels', worldIndex: Number(worldMatch[1]) };
    if (hash === 'daily') return { name: 'daily' };
    if (hash === 'daily/play') return { name: 'dailyPlay' };
    if (hash === 'endless') return { name: 'endless' };
    if (hash === 'account') return { name: 'account' };
    const glossaryMatch = hash.match(/^glossary(?:\/([a-z0-9-]+))?$/);
    if (glossaryMatch) return { name: 'glossary', slug: glossaryMatch[1] ?? null };
    if (hash === 'chronicle') return { name: 'chronicle' };
    if (hash === 'settings') return { name: 'settings' };
    const endlessMatch = hash.match(/^endless\/w(\d+)\/d(\d+)\/(\d+)$/);
    if (endlessMatch) {
      return {
        name: 'endlessPlay',
        setup: { worldIndex: Number(endlessMatch[1]), difficulty: Number(endlessMatch[2]) },
        seed: Number(endlessMatch[3]),
      };
    }
    return { name: 'worlds' };
  }

  private renderRoute(): void {
    this.activePuzzle?.unmount();
    this.activePuzzle = null;
    this.activeMap?.unmount();
    this.activeMap = null;

    const route = this.parseHash();
    if (!routeIsOpen(route)) {
      this.navigate('#/');
      return;
    }
    if (route.name !== 'worlds') this.homeTitleCompact = true;
    switch (route.name) {
      case 'worlds':
        this.renderWorlds();
        break;
      case 'levels':
        this.renderLevels(route.worldIndex);
        break;
      case 'puzzle':
        void this.renderPuzzle(route.worldIndex, route.levelIndex);
        break;
      case 'daily':
        this.renderDaily();
        break;
      case 'dailyPlay':
        this.renderDailyPuzzle();
        break;
      case 'endless':
        this.renderEndless();
        break;
      case 'endlessPlay':
        this.renderEndlessPuzzle(route.setup, route.seed);
        break;
      case 'account':
        this.renderAccount();
        break;
      case 'glossary':
        this.renderGlossary(route.slug);
        break;
      case 'chronicle':
        void this.renderChronicle();
        break;
      case 'settings':
        this.renderSettings();
        break;
    }
  }

  private renderWorlds(): void {
    const stars = totalStars(this.progress);
    const scene = gearfallMap();
    const largeTitle = !this.homeTitleCompact;
    this.root.innerHTML = `
      <div class="screen--stage screen--city${largeTitle ? ' has-large-title' : ''}">
        <section class="map map--city" aria-label="Map of Gearfall">
          <div class="stage map__frame">
            <canvas class="map__canvas" aria-hidden="true"></canvas>
            <ul class="map__pins">
              ${WORLDS.map((world) => this.districtPin(world, scene)).join('')}
            </ul>
          </div>
        </section>
        <header class="hud hud--top plate hero ${largeTitle ? 'hero--large' : 'hero--compact'}">
          <p class="kicker">A Gearfall Story</p>
          <h1 class="hero__title">Wirerunners<span class="hero__of">of</span>Gearfall</h1>
          ${ORNAMENT}
          <p class="hero__sub">Seven fronts. Seven algorithms. One rebellion.</p>
        </header>
        <div class="hud hud--tr hero__status">
          <p class="hero__stars">★ ${stars} ${stars === 1 ? 'star' : 'stars'} earned</p>
          <a class="hero__runner" href="#/settings" aria-label="Runner's kit: name, sound, account"><span class="hero__runner-name">${escapeHtml(this.runnerName())}</span><span class="hero__gear" aria-hidden="true">⚙</span></a>
          ${this.offlineMarkup()}
          ${this.accountLinkMarkup()}
        </div>
        ${this.boardMarkup()}
        <aside class="hud hud--bl plate dispatch">
          <img class="dispatch__portrait" src="story/portraits/kit.png" alt="" width="76" height="76" />
          <div class="dispatch__body">
            <p class="dispatch__from">Kit · Runner &amp; Resistance Lead</p>
            <p class="dispatch__line">“Optimal paths only. Welcome to the resistance.”</p>
          </div>
        </aside>
      </div>
    `;

    this.root.querySelectorAll<HTMLElement>('[data-world]').forEach((el) => {
      el.addEventListener('click', () => this.navigate(`#/world/${el.dataset.world}`));
    });
    this.mountMap(scene, themeFor('gearfall'));
  }

  private boardMarkup(): string {
    const challenge = dailyChallengeForToday();
    const world = worldById(challenge.worldIndex);
    const streak = dailyStreakFromProgress(this.progress);
    const done = starsFor(this.progress, challenge.levelId) > 0;
    const streakLine =
      streak.currentStreak > 0
        ? `${streak.currentStreak}-day streak · ${done ? 'delivered' : 'open'}`
        : done
          ? 'Delivered today'
          : 'Open today';
    const unsealed = unsealedCount(this.progress);
    const scenes = chronicleTally(chronicleChapters(seenCutscenes()));
    return `
      <nav class="hud hud--br board" aria-label="Notice board">
        <a class="board__card${done ? ' is-done' : ''}" href="#/daily">
          <span class="board__kicker">Daily dispatch<span class="board__date"> · ${escapeHtml(formatDailyDateShort(challenge))}</span></span>
          <b class="board__title">${escapeHtml(world ? problemTypeLabel(world.problemType) : 'Unknown')}</b>
          <i class="board__meta">${escapeHtml(streakLine)}</i>
        </a>
        <a class="board__card" href="#/endless">
          <span class="board__kicker">Endless runs</span>
          <b class="board__title">Generated districts</b>
          <i class="board__meta">Any front, any difficulty</i>
        </a>
        <a class="board__card" href="#/glossary">
          <span class="board__kicker">Compendium</span>
          <b class="board__title">Glossary of the trade</b>
          <i class="board__meta">${unsealed} of ${GLOSSARY.length} entries unsealed</i>
        </a>
        <a class="board__card" href="#/chronicle">
          <span class="board__kicker">Codex</span>
          <b class="board__title">Chronicle &amp; cast</b>
          <i class="board__meta">${scenes.seen} of ${scenes.total} scenes witnessed</i>
        </a>
      </nav>
    `;
  }

  private districtPin(world: World, scene: MapScene): string {
    const site = scene.graph.nodes.find((n) => n.id === worldPinId(world.id));
    const flip = (site?.position.x ?? 0) > 0.65 ? ' pin--flip' : '';
    if (!isWorldOpen(world.id)) {
      return `
      <li>
        <button class="pin pin--district is-closed${flip}" data-pin="${worldPinId(world.id)}" disabled
                aria-label="${escapeHtml(world.name)}, in preparation">
          <span class="pin__badge" aria-hidden="true"></span>
          <span class="pin__plate">
            <b>${escapeHtml(world.name)}</b>
            <i>${escapeHtml(problemTypeLabel(world.problemType))}</i>
            <em>In preparation</em>
          </span>
        </button>
      </li>
    `;
    }
    const earned = worldStars(this.progress, world.id);
    const max = world.totalLevels * 3;
    const state = earned >= max ? ' is-clear' : earned > 0 ? ' is-started' : '';
    return `
      <li>
        <button class="pin pin--district${state}${flip}" data-world="${world.id}" data-pin="${worldPinId(world.id)}"
                aria-label="${escapeHtml(world.name)}, ${escapeHtml(problemTypeLabel(world.problemType))}, ${earned} of ${max} stars">
          <span class="pin__badge">${world.id}</span>
          <span class="pin__plate">
            <b>${escapeHtml(world.name)}</b>
            <i>${escapeHtml(problemTypeLabel(world.problemType))}</i>
            <em>★ ${earned} / ${max}</em>
          </span>
        </button>
      </li>
    `;
  }

  private renderLevels(worldIndex: number): void {
    const world = worldById(worldIndex);
    if (!world) {
      this.navigate('#/');
      return;
    }

    void this.playStoryBeatOnce(worldCutsceneId(worldIndex, 'intro'), () => {
      const route = this.parseHash();
      return route.name === 'levels' && route.worldIndex === worldIndex;
    });

    const pins = Array.from({ length: world.totalLevels }, (_, i) => {
      const index = i + 1;
      const id = levelId(worldIndex, index);
      const unlocked = isUnlocked(this.progress, worldIndex, index);
      const stars = starsFor(this.progress, id);
      const state = !unlocked
        ? ' is-locked'
        : stars === 3
          ? ' is-clear'
          : stars > 0
            ? ' is-started'
            : '';
      const label = unlocked ? `Run ${index}, ${stars} of 3 stars` : `Run ${index}, locked`;
      return `
        <li>
          <button class="pin pin--run${state}" data-level="${index}" data-pin="${runPinId(index)}"
                  aria-label="${label}" ${unlocked ? '' : 'disabled'}>
            <span class="pin__badge">${index}</span>
            <span class="pin__stars">${unlocked ? '★'.repeat(stars) + '☆'.repeat(3 - stars) : '🔒'}</span>
          </button>
        </li>
      `;
    }).join('');

    this.root.innerHTML = `
      <div class="screen--stage screen--district">
        <section class="map map--district" aria-label="Runs in ${escapeHtml(world.name)}">
          <div class="stage map__frame">
            <canvas class="map__canvas" aria-hidden="true"></canvas>
            <ul class="map__pins">${pins}</ul>
          </div>
        </section>
        <div class="hud hud--tl district__head">
          <header class="plate sub-header">
            <button class="btn btn--ghost btn--icon" data-action="back" aria-label="Back to the map">←</button>
            <div>
              <h1>${escapeHtml(world.name)}</h1>
              <p>${escapeHtml(problemTypeLabel(world.problemType))}</p>
            </div>
          </header>
          <p class="plate district__brief">${escapeHtml(world.description)}</p>
        </div>
      </div>
    `;

    this.root
      .querySelector('[data-action="back"]')
      ?.addEventListener('click', () => this.navigate('#/'));
    this.root.querySelectorAll<HTMLElement>('[data-level]').forEach((el) => {
      el.addEventListener('click', () =>
        this.navigate(`#/world/${worldIndex}/level/${el.dataset.level}`),
      );
    });
    this.mountMap(districtScene(world), themeFor(world.themeId));
  }

  private mountMap(scene: MapScene, theme: MapTheme): void {
    const frame = this.root.querySelector<HTMLElement>('.map__frame');
    if (!frame) return;
    this.activeMap = new MapPanel(frame, scene, theme);
    this.activeMap.mount();
  }

  /** Paper screens: the markup scrolls on a sheet above the dimmed city. */
  private renderSheet(markup: string): void {
    this.activeMap?.unmount();
    this.activeMap = null;
    this.root.innerHTML = `${BACKDROP}<div class="sheet">${markup}</div>`;
    this.mountBackdrop();
  }

  private mountBackdrop(): void {
    const stage = this.root.querySelector<HTMLElement>('.stage--dim');
    if (!stage) return;
    this.activeMap = new MapPanel(stage, gearfallMap(), themeFor('gearfall'));
    this.activeMap.mount();
  }

  /** Keeps whatever stage is showing (the solved puzzle, usually) and dims it under a sheet. */
  private dimStage(): void {
    const stage = this.root.querySelector<HTMLElement>('.stage');
    if (!stage) {
      this.root.innerHTML = BACKDROP;
      this.mountBackdrop();
      return;
    }
    stage.classList.add('stage--dim');
    this.root.querySelectorAll<HTMLElement>('.hud').forEach((el) => {
      el.hidden = true;
    });
  }

  private async renderPuzzle(worldIndex: number, levelIndex: number): Promise<void> {
    const world = worldById(worldIndex);
    if (!world) {
      this.navigate('#/');
      return;
    }

    this.root.innerHTML = '<div class="screen screen--loading">Charting the district…</div>';

    const briefing = this.briefingFor(worldIndex, levelIndex);
    let level: Level;
    try {
      level = await loadLevel(worldIndex, levelIndex);
    } catch (error) {
      this.root.innerHTML = `
        <div class="screen">
          <p class="error">Could not load level ${levelIndex} of world ${worldIndex}.</p>
          <p class="error__detail">${escapeHtml(String(error))}</p>
          <button class="btn btn--primary" data-action="back">Back</button>
        </div>`;
      this.root
        .querySelector('[data-action="back"]')
        ?.addEventListener('click', () => this.navigate(`#/world/${worldIndex}`));
      return;
    }

    const cards = await briefing;
    if (!this.isPuzzleRoute(worldIndex, levelIndex)) return;
    this.mountPuzzle(
      level,
      world.themeId,
      { kind: 'campaign', worldIndex },
      `#/world/${worldIndex}`,
      { briefing: cards },
    );
  }

  private isPuzzleRoute(worldIndex: number, levelIndex: number): boolean {
    const route = this.parseHash();
    return (
      route.name === 'puzzle' && route.worldIndex === worldIndex && route.levelIndex === levelIndex
    );
  }

  /**
   * First-run cards for a world's level 1, until the player has read them once.
   * When this device has not, the account is asked first: another device may have.
   */
  private async briefingFor(
    worldIndex: number,
    levelIndex: number,
  ): Promise<readonly TutorialCard[] | undefined> {
    const cards = tutorialCardsForLevel(worldIndex, levelIndex);
    if (!cards) return undefined;
    const id = tutorialId(worldIndex);
    if (!hasSeenCutscene(id)) await this.sessionReady;
    return hasSeenCutscene(id) ? undefined : cards;
  }

  private mountPuzzle(
    level: Level,
    themeId: string,
    context: RunContext,
    exitHash: string,
    presentation: PuzzlePresentation = {},
  ): void {
    const screen = new PuzzleScreen(
      this.root,
      level,
      themeId,
      {
        onExit: () => this.navigate(exitHash),
        onSolved: (result) => {
          this.activePuzzle?.unmount();
          this.activePuzzle = null;
          void this.persistResult(result, context);
        },
        onBriefingRead: () => void recordCutsceneSeen(tutorialId(level.worldIndex)),
      },
      presentation,
    );
    this.activePuzzle = screen;
    screen.mount();
  }

  private renderDaily(): void {
    this.renderSheet(dailyBriefingMarkup({ progress: this.progress, online: this.online }));
    this.root
      .querySelector('[data-action="back"]')
      ?.addEventListener('click', () => this.navigate('#/'));
    this.root
      .querySelector('[data-action="play"]')
      ?.addEventListener('click', () => this.navigate('#/daily/play'));
  }

  private renderDailyPuzzle(): void {
    const challenge = dailyChallengeForToday();
    const world = worldById(challenge.worldIndex);
    if (!world) {
      this.navigate('#/daily');
      return;
    }
    const level = generateDailyChallengeLevel(challenge);
    const day = challenge.date.getUTCDate();
    const monthYear = formatDailyDate(challenge).split(' ').slice(2).join(' ');
    this.mountPuzzle(level, world.themeId, { kind: 'daily', challenge }, '#/daily', {
      kicker: `Daily dispatch · ${world.name}`,
      calendar: calendarSheet('Dispatch', String(day), monthYear, formatDailyDate(challenge)),
    });
  }

  private renderEndless(): void {
    this.renderSheet(endlessSetupMarkup(loadEndlessSetup()));
    this.root
      .querySelector('[data-action="back"]')
      ?.addEventListener('click', () => this.navigate('#/'));

    const form = this.root.querySelector<HTMLFormElement>('[data-role="setup"]');
    if (!form) return;
    const rank = form.querySelector<HTMLElement>('[data-role="rank"]');
    form.addEventListener('input', () => {
      const setup = readEndlessSetup(form);
      form.querySelectorAll<HTMLElement>('.setup__front').forEach((el) => {
        const input = el.querySelector<HTMLInputElement>('input');
        el.classList.toggle('is-selected', input?.checked === true);
      });
      if (rank)
        rank.innerHTML = `<b>${setup.difficulty}</b> <i>${escapeHtml(rankFor(setup.difficulty))}</i>`;
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const setup = readEndlessSetup(form);
      saveEndlessSetup(setup);
      this.navigate(endlessHash(setup, newSeed()));
    });
  }

  private renderEndlessPuzzle(setup: EndlessSetup, seed: number): void {
    const world = worldById(setup.worldIndex);
    if (!world || setup.difficulty < 1 || setup.difficulty > 10) {
      this.navigate('#/endless');
      return;
    }
    let level: Level;
    try {
      level = generateRandomLevel({
        worldIndex: setup.worldIndex,
        difficulty: setup.difficulty,
        seed,
      });
    } catch {
      this.navigate('#/endless');
      return;
    }
    this.mountPuzzle(level, world.themeId, { kind: 'endless', setup, seed }, '#/endless', {
      kicker: `Endless run · ${world.name}`,
      calendar: calendarSheet(
        'Endless',
        String(setup.difficulty),
        rankFor(setup.difficulty),
        `Difficulty ${setup.difficulty}, ${rankFor(setup.difficulty)}`,
      ),
    });
  }

  private async persistResult(result: PuzzleResult, context: RunContext): Promise<void> {
    const recorded = await recordRun(
      this.progress,
      {
        levelId: result.level.id,
        selectedEdgeIds: result.selectedEdgeIds,
        ...(result.edgeFlows ? { edgeFlows: result.edgeFlows } : {}),
        durationMs: result.durationMs,
        hintsUsed: result.hintsUsed,
        undoCount: result.undoCount,
        edgesSelectedDuringSession: result.edgesSelectedDuringSession,
      },
      result.score,
      result.stars,
    );

    this.progress = recorded.progress;
    this.online = recorded.online;
    this.renderResults(
      { ...result, score: recorded.score, stars: recorded.stars },
      context,
      recorded.unlockedAchievements,
    );
  }

  private unlockedMarkup(unlockedAchievements: readonly string[]): string {
    if (unlockedAchievements.length === 0) return '';

    const titles = unlockedAchievements
      .map((id) => achievementById(id)?.title ?? id)
      .map((title) => `<li>${escapeHtml(title)}</li>`)
      .join('');
    return `<div class="result__unlocked"><p>Unlocked</p><ul>${titles}</ul></div>`;
  }

  private offlineMarkup(): string {
    return this.online
      ? ''
      : '<p class="hero__offline">Offline — progress is saved on this device only.</p>';
  }

  private accountLinkMarkup(): string {
    if (!this.online) return '';
    const registered = this.user !== null && !this.user.isGuest;
    const label = registered
      ? `Signed in as ${this.user?.email ?? 'runner'}`
      : 'Guest runner · register to keep your progress';
    return `<a class="hero__account" href="#/account">${escapeHtml(label)}</a>`;
  }

  private renderGlossary(focusSlug: string | null): void {
    this.renderSheet(glossaryMarkup({ progress: this.progress, focusSlug }));
    this.root
      .querySelector('[data-action="back"]')
      ?.addEventListener('click', () => this.navigate('#/'));
    const search = this.root.querySelector<HTMLInputElement>('[data-role="search"]');
    search?.addEventListener('input', () => filterGlossaryList(this.root, search.value));
    this.root.querySelector('.entry.is-focused')?.scrollIntoView({ block: 'center' });
  }

  private async renderChronicle(): Promise<void> {
    const chapters = chronicleChapters(seenCutscenes());
    this.renderSheet(chronicleMarkup({ chapters }));
    this.root
      .querySelector('[data-action="back"]')
      ?.addEventListener('click', () => this.navigate('#/'));
    this.root.querySelectorAll<HTMLButtonElement>('[data-cutscene]').forEach((el) => {
      el.addEventListener('click', () => {
        el.disabled = true;
        void playCutscene(el.dataset['cutscene']!)
          .catch(() => {})
          .then(() => {
            el.disabled = false;
          });
      });
    });

    const [roster, spoken] = await Promise.all([
      loadCharacters().catch(() => EMPTY_ROSTER),
      spokenCharacters(chapters, loadCutscene),
    ]);
    const cast = this.root.querySelector<HTMLElement>('[data-role="cast"]');
    if (cast) cast.outerHTML = castMarkup(roster, spoken);
  }

  private runnerName(): string {
    return this.user?.displayName ?? ensureRunnerName();
  }

  private renderSettings(): void {
    this.renderSheet(
      settingsMarkup({
        runnerName: this.runnerName(),
        soundOn: audio.enabled,
        user: this.user,
        online: this.online,
      }),
    );
    this.root
      .querySelector('[data-action="back"]')
      ?.addEventListener('click', () => this.navigate('#/'));

    const form = this.root.querySelector<HTMLFormElement>('[data-form="runner"]')!;
    const input = form.querySelector<HTMLInputElement>('input[name="runnerName"]')!;
    form.querySelector('[data-action="suggest"]')?.addEventListener('click', () => {
      input.value = suggestCallsign();
      showFormStatus(form, null);
      input.focus();
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitRunnerName(form, input.value);
    });

    const sound = this.root.querySelector<HTMLInputElement>('[data-role="sound"]');
    sound?.addEventListener('change', () => {
      audio.setEnabled(sound.checked);
      if (sound.checked) audio.play('edgeSelect');
    });
  }

  private async submitRunnerName(form: HTMLFormElement, raw: string): Promise<void> {
    const name = normalizeRunnerName(raw);
    if (name === null) {
      showFormError(form, 'A name needs one to forty characters.');
      return;
    }
    showFormError(form, null);
    showFormStatus(form, null);
    setFormBusy(form, true);
    const result = await renameRunner(name, {
      progress: this.progress,
      online: this.online,
      user: this.user,
    });
    setFormBusy(form, false);
    if (!result.ok) {
      showFormError(form, `${result.message} The name is kept on this device.`);
      return;
    }
    this.adopt(result.value);
    form.querySelector<HTMLInputElement>('input[name="runnerName"]')!.value = name;
    showFormStatus(form, `Noted. You run as ${name}.`);
  }

  private renderAccount(): void {
    this.renderSheet(accountMarkup({ user: this.user, online: this.online }));
    this.root
      .querySelector('[data-action="back"]')
      ?.addEventListener('click', () => this.navigate('#/'));

    const register = this.root.querySelector<HTMLFormElement>('[data-form="register"]');
    register?.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitCredentials(register, async (email, password) => {
        const result = await registerAccount(email, password);
        if (!result.ok) return result.message;
        this.user = result.value;
        return null;
      });
    });

    const signin = this.root.querySelector<HTMLFormElement>('[data-form="signin"]');
    signin?.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitCredentials(signin, async (email, password) => {
        const result = await signIn(email, password);
        if (!result.ok) return result.message;
        this.adopt(result.value);
        return null;
      });
    });

    const signout = this.root.querySelector<HTMLButtonElement>('[data-action="signout"]');
    signout?.addEventListener('click', () => {
      signout.disabled = true;
      void signOut().then((state) => {
        this.adopt(state);
        this.renderAccount();
      });
    });
  }

  private async submitCredentials(
    form: HTMLFormElement,
    attempt: (email: string, password: string) => Promise<string | null>,
  ): Promise<void> {
    const credentials = readCredentials(form);
    if (typeof credentials === 'string') {
      showFormError(form, credentials);
      return;
    }
    showFormError(form, null);
    setFormBusy(form, true);
    const error = await attempt(credentials.email, credentials.password);
    if (error !== null) {
      setFormBusy(form, false);
      showFormError(form, error);
      return;
    }
    this.renderAccount();
  }

  private renderResults(
    result: PuzzleResult,
    context: RunContext,
    unlockedAchievements: readonly string[] = [],
  ): void {
    const { level, score, stars } = result;
    const seal = ['RETURNED', 'DELIVERED', 'WELL RUN', 'MASTERWORK'][stars] ?? 'RETURNED';

    for (let i = 0; i < stars; i++) {
      window.setTimeout(() => audio.play('starEarned'), 260 + i * 320);
    }

    const report = this.reportFor(context, result);
    const sheet = document.createElement('div');
    sheet.className = 'sheet sheet--center';
    sheet.innerHTML = `
      <article class="result">
        <span class="note__staple" aria-hidden="true"></span>
        <header class="result__head">
          <p class="note__kicker">${escapeHtml(report.kicker)}</p>
          ${report.calendar}
        </header>
        <p class="result__seal">${seal}</p>
        <h1 class="result__title">${escapeHtml(level.title)}</h1>
        <p class="result__stars" aria-label="${stars} of 3 stars">${[0, 1, 2].map((i) => `<span class="result__star${i < stars ? ' is-earned' : ''}"></span>`).join('')}</p>
        <div class="result__row">
          ${watchMarkup(score, level.optimalValue)}
          <dl class="result__figures">
            <div><dt>Your result</dt><dd>${formatNumber(score)}</dd></div>
            <div><dt>Optimum</dt><dd>${formatNumber(level.optimalValue)}</dd></div>
          </dl>
        </div>
        ${report.extra}
        ${this.unlockedMarkup(unlockedAchievements)}
        <div class="result__actions">
          ${report.actions.map((a) => `<button class="btn ${a.primary ? 'btn--primary' : 'btn--ghost'}" data-hash="${escapeHtml(a.hash)}">${escapeHtml(a.label)}</button>`).join('')}
        </div>
      </article>
    `;
    this.dimStage();
    this.root.appendChild(sheet);

    sheet.querySelectorAll<HTMLElement>('[data-hash]').forEach((el) => {
      el.addEventListener('click', () => this.navigate(el.dataset.hash!));
    });
  }

  private reportFor(context: RunContext, result: PuzzleResult): RunReport {
    const { level, stars } = result;
    switch (context.kind) {
      case 'campaign': {
        const world = worldById(context.worldIndex);
        const hasNext = world ? level.levelIndex < world.totalLevels : false;
        if (world && level.levelIndex >= world.totalLevels && stars > 0) {
          void this.playWorldOutro(context.worldIndex);
        }
        const term = glossaryTermForProblemType(level.type);
        return {
          kicker: `Run report · ${world?.name ?? 'Gearfall'}`,
          calendar: calendarMarkup(level.levelIndex, world?.totalLevels ?? level.levelIndex),
          extra: `<p class="result__readup">Read up on <a href="${glossaryHash(term)}">${escapeHtml(term)}</a> in the Engineer's Compendium.</p>`,
          actions: [
            {
              label: 'Play again',
              hash: `#/world/${context.worldIndex}/level/${level.levelIndex}`,
            },
            { label: 'All runs', hash: `#/world/${context.worldIndex}` },
            ...(hasNext
              ? [
                  {
                    label: 'Next run',
                    hash: `#/world/${context.worldIndex}/level/${level.levelIndex + 1}`,
                    primary: true,
                  },
                ]
              : []),
          ],
        };
      }
      case 'daily': {
        const { challenge } = context;
        const streak = dailyStreakFromProgress(this.progress);
        return {
          kicker: `Daily dispatch · ${formatDailyDate(challenge)}`,
          calendar: calendarSheet(
            'Streak',
            String(streak.currentStreak),
            streak.currentStreak === 1 ? 'day' : 'days',
            `${streak.currentStreak}-day streak`,
          ),
          extra: `<div class="result__streak">${streakMarkup(streak)}</div>`,
          actions: [
            { label: 'Run it again', hash: '#/daily/play' },
            { label: 'Back to Gearfall', hash: '#/', primary: true },
          ],
        };
      }
      case 'endless': {
        const { setup, seed } = context;
        const world = worldById(setup.worldIndex);
        return {
          kicker: `Endless run · ${world?.name ?? 'Gearfall'}`,
          calendar: calendarSheet(
            'Endless',
            String(setup.difficulty),
            rankFor(setup.difficulty),
            `Difficulty ${setup.difficulty}`,
          ),
          extra: '',
          actions: [
            { label: 'Retry this run', hash: endlessHash(setup, seed) },
            { label: 'Change setup', hash: '#/endless' },
            { label: 'Next run', hash: endlessHash(setup, newSeed()), primary: true },
          ],
        };
      }
    }
  }
}

interface RunReport {
  readonly kicker: string;
  readonly calendar: string;
  readonly extra: string;
  readonly actions: readonly { label: string; hash: string; primary?: boolean }[];
}

function routeIsOpen(route: Route): boolean {
  if (route.name === 'levels' || route.name === 'puzzle') return isWorldOpen(route.worldIndex);
  if (route.name === 'endlessPlay') return isWorldOpen(route.setup.worldIndex);
  return true;
}

function watchMarkup(score: number, optimum: number): string {
  const exposure = exposureOf(score, optimum);
  const sweep = exposure * 360;
  const cx = 60;
  const cy = 78;
  const r = 44;
  const at = (angle: number, radius: number): { x: string; y: string } => {
    const rad = ((angle - 90) * Math.PI) / 180;
    return {
      x: (cx + radius * Math.cos(rad)).toFixed(1),
      y: (cy + radius * Math.sin(rad)).toFixed(1),
    };
  };
  const line = (angle: number, from: number, to: number, cls = ''): string => {
    const a = at(angle, from);
    const b = at(angle, to);
    return `<line class="${cls}" x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}" />`;
  };
  const start = at(0, r);
  const stop = at(sweep, r);
  const sector =
    sweep >= 360
      ? `<circle cx="${cx}" cy="${cy}" r="${r}" class="watch__sector" />`
      : sweep > 0
        ? `<path class="watch__sector" d="M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${sweep > 180 ? 1 : 0} 1 ${stop.x} ${stop.y} Z" />`
        : '';
  const ticks = Array.from({ length: 12 }, (_, i) => line(i * 30, r - 2, r - 8)).join('');
  const hand = line(Math.min(sweep, 359.9), 0, r - 12, 'watch__hand');
  const label = exposure === 0 ? 'none' : `${Math.round(exposure * 100)} %`;
  return `
    <figure class="watch">
      <svg viewBox="0 0 120 134" aria-hidden="true">
        <circle cx="${cx}" cy="10" r="6" class="watch__bow" />
        <rect x="54" y="15" width="12" height="9" rx="2" class="watch__stem" />
        <circle cx="${cx}" cy="${cy}" r="${r + 9}" class="watch__case" />
        <circle cx="${cx}" cy="${cy}" r="${r + 2}" class="watch__face" />
        ${sector}
        <g class="watch__ticks">${ticks}</g>
        ${hand}
        <circle cx="${cx}" cy="${cy}" r="3" class="watch__pin" />
      </svg>
      <figcaption>Exposure<b>${label}</b></figcaption>
    </figure>
  `;
}

function exposureOf(score: number, optimum: number): number {
  if (!Number.isFinite(score) || !Number.isFinite(optimum)) return 1;
  const worse = Math.max(score, optimum);
  if (worse === 0) return 0;
  return Math.min(1, Math.max(0, 1 - Math.min(score, optimum) / worse));
}

function formatNumber(value: number): string {
  if (value === Infinity) return '∞';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
